import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import DropBox from "../upload-file-from-computer/drop-box.component";
import { grey } from "@mui/material/colors";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Patient } from "services/types/patients.type";
import UploadFilesFromEDocsModal from "./upload-files-from-edocs-modal.component";
import { selectCurrentClinic } from "features/auth/auth-slice";
import { useAppSelector } from "app/hooks";
import { useGetClinicQuery } from "services/clinics";

type UploadFilesFromEDocsProps = {
  acceptedType?: string;
  boxAccessToken: string;
  label?: ReactNode;
  sizeLimitLabel?: ReactNode;
  patient?: Patient | null;
  fileSizeLimit?: number;
  onPatientSelected?: (patient: Patient | null) => void;
  onFilesSelected: (selectedFiles: File[]) => void;
  onChangeLoadingState?: (isLoading: boolean) => void;
};
export default function UploadFilesFromEDocs({
  acceptedType,
  boxAccessToken,
  fileSizeLimit,
  label,
  sizeLimitLabel,
  patient,
  onPatientSelected,
  onFilesSelected,
  onChangeLoadingState,
}: UploadFilesFromEDocsProps) {
  const currentClinic = useAppSelector(selectCurrentClinic);

  const { data: clinic } = useGetClinicQuery(
    { clinicId: currentClinic?.id! },
    {
      skip: !Boolean(currentClinic?.id),
      refetchOnMountOrArgChange: true,
    }
  );
  const clinicLogo = useMemo(
    () => clinic?.logo || clinic?.practice?.logo || "",
    [clinic]
  );

  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, []);

  const isDisabled = patient && !patient?.box_folder_id;
  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <>
      <DropBox
        sx={{
          display: "flex",
          justifyContent: "center",
          cursor: !isDisabled ? "pointer" : undefined,
          opacity: isDisabled ? 0.5 : 1,
          "&.MuiBox-root:hover, &.MuiBox-root.dragover": {
            opacity: isDisabled ? 0.5 : 0.6,
          },
        }}
        onClick={!isDisabled ? handleClick : undefined}
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            position: "relative",
            width: "240px",
            height: "6rem",
            boxSizing: "border-box",
            borderRadius: "12px",
          }}
        >
          <Stack justifyContent="center" sx={{ p: 1, textAlign: "center" }}>
            <div>
              <CloudUploadIcon />
            </div>
            <Stack>
              <Typography variant="body2" component="span">
                {label}
              </Typography>
              <Typography variant="body1" component="span" color={grey[600]}>
                {sizeLimitLabel}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </DropBox>
      <UploadFilesFromEDocsModal
        open={open}
        patient={patient}
        boxAccessToken={boxAccessToken}
        fileSizeLimit={fileSizeLimit}
        acceptedType={acceptedType}
        clinicLogo={clinicLogo}
        onPatientSelected={onPatientSelected}
        onFilesSelected={onFilesSelected}
        handleClose={handleClose}
        onChangeLoadingState={onChangeLoadingState}
      />
    </>
  );
}
